import { useCallback, useEffect, useRef, useState } from 'react';

const PAGE_SELECTOR = 'section.docx';
const ACTIVE_PAGE_OFFSET_RATIO = 1 / 3;

/** Applies zoom to the rendered document and tracks which page is in view. */
export const useDocxPages = (frameDoc: Document | null, scale: number) => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pagesRef = useRef<HTMLElement[]>([]);

  const updateCurrentPage = useCallback(() => {
    const viewportHeight = frameDoc?.defaultView?.innerHeight ?? 0;
    const threshold = viewportHeight * ACTIVE_PAGE_OFFSET_RATIO;
    let page = 1;
    pagesRef.current.forEach((el, index) => {
      if (el.getBoundingClientRect().top <= threshold) page = index + 1;
    });
    setCurrentPage(page);
  }, [frameDoc]);

  useEffect(() => {
    if (!frameDoc) return;
    pagesRef.current = Array.from(
      frameDoc.querySelectorAll<HTMLElement>(PAGE_SELECTOR)
    );
    setNumPages(pagesRef.current.length);
    frameDoc.addEventListener('scroll', updateCurrentPage, { passive: true });
    return () => frameDoc.removeEventListener('scroll', updateCurrentPage);
  }, [frameDoc, updateCurrentPage]);

  useEffect(() => {
    if (!frameDoc) return;
    frameDoc.body.style.zoom = String(scale);
    updateCurrentPage();
  }, [frameDoc, scale, updateCurrentPage]);

  const scrollToPage = useCallback((page: number) => {
    pagesRef.current[page - 1]?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return { numPages, currentPage, scrollToPage };
};
